import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, Plus, Trash, PencilSimple, MapPin, UserCircle, Check, Clock, X, FilePdf, Link, CalendarPlus, Notepad } from "@phosphor-icons/react";
import {
  listAppointments, createAppointment, updateAppointment, deleteAppointment, listPrescriptions,
  getAppointmentIcsUrl, downloadIcs,
} from "@/lib/api";
import { APPOINTMENT_TYPES, appointmentTypeMeta, apptDate, apptStatus, formatRelativeDay } from "@/lib/reminderUtils";
import { toLocalDatetimeInputValue } from "@/lib/dateUtils";
import { generateExamPacket } from "@/lib/examPacketPdf";
import { createExamWorksheetPdf } from "@/lib/examWorksheetPdf";
import { saveBytesAsPdf } from "@/lib/claimUtils";
import { showToast } from "@/lib/toast";

const STATUS_LABEL = {
  overdue: "OVERDUE",
  due_soon: "UPCOMING",
  ok: "SCHEDULED",
  completed: "COMPLETED",
  unknown: "—",
};
const STATUS_CLASS = {
  overdue: "rem-status rem-status--overdue",
  due_soon: "rem-status rem-status--due",
  ok: "rem-status rem-status--ok",
  completed: "rem-status rem-status--completed",
  unknown: "rem-status",
};

function emptyAppt(defaultLinkedIssueId) {
  const now = new Date();
  now.setMinutes(0, 0, 0);
  now.setHours(now.getHours() + 24);
  return {
    title: "",
    type: "va_visit",
    // Use the user's LOCAL wall clock for the datetime-local input default.
    // `toISOString().slice(0, 16)` returns UTC and the input ends up off
    // by the user's full timezone offset.
    datetime: toLocalDatetimeInputValue(now),
    provider: "",
    location: "",
    notes: "",
    remindDaysBefore: 1,
    completed: false,
    // Smart default: pre-link the most-recently-edited condition so the user doesn't have to
    // scroll the chip picker every time. They can unlink it in one tap if it's wrong.
    linkedIssueIds: defaultLinkedIssueId ? [defaultLinkedIssueId] : [],
  };
}

function AppointmentForm({ initial, onCancel, onSave, issues = [], defaultLinkedIssueId = null }) {
  const [form, setForm] = useState(initial || emptyAppt(defaultLinkedIssueId));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const sortedIssues = useMemo(
    () => [...issues].sort((a, b) => (a.claimedAs || "").localeCompare(b.claimedAs || "")),
    [issues],
  );

  function patch(k, v) { setForm((p) => ({ ...p, [k]: v })); }
  function toggleIssue(id) {
    const cur = form.linkedIssueIds || [];
    patch("linkedIssueIds", cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  }

  async function submit() {
    if (!form.title.trim()) { setErr("Title is required."); return; }
    if (!form.datetime) { setErr("Date and time required."); return; }
    setBusy(true); setErr("");
    try {
      // Backend expects ISO 8601. datetime-local string already serializes OK.
      const payload = { ...form, datetime: new Date(form.datetime).toISOString() };
      await onSave(payload);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" data-testid="appt-form-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="modal-panel modal-panel--lg">
        <div className="modal-header">
          <Calendar size={16} weight="bold" />
          <span>{initial?.id ? "EDIT APPOINTMENT" : "NEW APPOINTMENT"}</span>
          <button className="modal-close" onClick={onCancel}><X size={14} weight="bold" /></button>
        </div>
        <div className="modal-body modal-body--scroll">
          <div className="form-grid">
            <label className="field field--full">
              <span className="field-label">TITLE *</span>
              <input className="field-input" value={form.title} onChange={(e) => patch("title", e.target.value)} placeholder="e.g., C&P exam for PTSD" data-testid="appt-title" />
            </label>
            <label className="field">
              <span className="field-label">TYPE</span>
              <select className="field-input" value={form.type} onChange={(e) => patch("type", e.target.value)} data-testid="appt-type">
                {APPOINTMENT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </label>
            <label className="field">
              <span className="field-label">DATE & TIME *</span>
              <input type="datetime-local" className="field-input" value={form.datetime?.slice(0, 16) || ""} onChange={(e) => patch("datetime", e.target.value)} data-testid="appt-datetime" />
            </label>
            <label className="field">
              <span className="field-label">PROVIDER</span>
              <input className="field-input" value={form.provider} onChange={(e) => patch("provider", e.target.value)} placeholder="Dr. name or VA clinic" data-testid="appt-provider" />
            </label>
            <label className="field">
              <span className="field-label">LOCATION</span>
              <input className="field-input" value={form.location} onChange={(e) => patch("location", e.target.value)} placeholder="VAMC, address, or Telehealth" data-testid="appt-location" />
            </label>
            <label className="field">
              <span className="field-label">REMIND ME (DAYS BEFORE)</span>
              <input type="number" min="0" max="30" className="field-input mono" value={form.remindDaysBefore} onChange={(e) => patch("remindDaysBefore", Number(e.target.value))} data-testid="appt-remind" />
            </label>
            <label className="field field--full">
              <span className="field-label">NOTES</span>
              <textarea className="field-input" rows={3} value={form.notes} onChange={(e) => patch("notes", e.target.value)} placeholder="What to bring, questions to ask, prep details..." data-testid="appt-notes" />
            </label>
            {issues.length > 0 && (
              <label className="field field--full">
                <span className="field-label">LINK TO CONDITIONS (CHOOSE ONE OR MORE)</span>
                <div className="appt-link-chips" data-testid="appt-link-issues">
                  {sortedIssues.map((i) => {
                    const active = (form.linkedIssueIds || []).includes(i.id);
                    return (
                      <button
                        type="button"
                        key={i.id}
                        className={`appt-link-chip ${active ? "appt-link-chip--active" : ""}`}
                        onClick={() => toggleIssue(i.id)}
                        data-testid={`appt-link-${i.id}`}
                      >
                        {active ? <Check size={11} weight="bold" /> : <Link size={11} weight="bold" />}
                        <span>{i.claimedAs || `DC ${i.code}`}</span>
                      </button>
                    );
                  })}
                </div>
                <span className="field-hint">Linking conditions lets the exam packet zero-in on what to discuss at this appointment.</span>
              </label>
            )}
          </div>
          {err && <div className="form-report form-report--err" data-testid="appt-error">{err}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn--outline" onClick={onCancel} disabled={busy}>CANCEL</button>
          <button className="btn btn--accent" onClick={submit} disabled={busy} data-testid="appt-save">
            <Check size={14} weight="bold" /> {busy ? "Saving..." : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AppointmentsView({
  issues = [],
  evidence = [],
  profile = {},
  workspaceTitle = "",
  workspace = null,
  master526ez = null,
  isFree = false,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all"); // all | upcoming | past | completed
  const [generating, setGenerating] = useState("");  // id of appointment being PDF'd

  const load = useCallback(async () => {
    setLoading(true);
    setItems(await listAppointments());
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function handleSave(payload) {
    if (editing?.id) {
      await updateAppointment(editing.id, payload);
    } else {
      await createAppointment(payload);
    }
    setShowForm(false); setEditing(null);
    await load();
  }

  async function handleDelete(item) {
    if (!window.confirm(`Delete appointment "${item.title}"?`)) return;
    await deleteAppointment(item.id);
    await load();
    // Offer one-click UNDO so a misclick doesn't vaporize an upcoming exam.
    showToast({
      message: `Appointment "${item.title}" deleted.`,
      duration: 6000,
      action: {
        label: "UNDO",
        onClick: async () => {
          try {
            // The backend assigns a new id on create; that's fine — we just need the data back.
            const { id, user_id, ...payload } = item; // strip identifiers
            void id; void user_id;
            await createAppointment(payload);
            await load();
            showToast({ variant: "success", message: "Appointment restored.", duration: 3000 });
          } catch (e) {
            showToast({ variant: "error", message: `Restore failed: ${e.message}` });
          }
        },
      },
    });
  }

  async function toggleCompleted(item) {
    await updateAppointment(item.id, { completed: !item.completed });
    await load();
  }

  async function handleGeneratePacket(appt) {
    setGenerating(appt.id);
    try {
      // Always pull the latest prescriptions snapshot so the packet is current
      const prescriptions = await listPrescriptions();
      await generateExamPacket({
        appointment: appt,
        conditions: issues,
        evidence,
        prescriptions,
        profile,
        workspaceTitle,
        workspace,
        master526ez,
        isFree,
      });
    } catch (e) {
      window.alert(`Could not generate packet: ${e.message}`);
    } finally {
      setGenerating("");
    }
  }

  async function handleGenerateWorksheets(appt) {
    setGenerating(`ws-${appt.id}`);
    try {
      const prescriptions = await listPrescriptions();
      // Pick exam variant from appointment type
      const examType = appt.type === "cp_exam"
        ? "cp"
        : appt.type === "ime" || appt.type === "civilian_visit"
        ? "ime"
        : "routine";
      // Filter conditions to the ones linked to this appointment (fall back to all
      // if the veteran hasn't linked any conditions on the appointment yet).
      const linkedIds = appt.linkedIssueIds || [];
      const linkedConditions = linkedIds.length > 0
        ? issues.filter((i) => linkedIds.includes(i.id))
        : issues;
      const bytes = await createExamWorksheetPdf({
        veteranName: profile.name || "Veteran",
        master526ez: { dateOfBirth: profile.dateOfBirth || "" },
        appointment: appt,
        conditions: linkedConditions,
        prescriptions,
        examType,
      });
      const safeName = (profile.name || "Veteran").replace(/[^a-zA-Z0-9-_]+/g, "-");
      const stamp = new Date().toISOString().slice(0, 10);
      saveBytesAsPdf(bytes, `ClaimOps-Exam-Worksheets-${safeName}-${stamp}.pdf`);
      showToast({ variant: "success", duration: 3000, message: `Generated ${linkedConditions.length} worksheet${linkedConditions.length === 1 ? "" : "s"}.` });
    } catch (e) {
      showToast({ variant: "error", message: `Could not generate worksheets: ${e.message}` });
    } finally {
      setGenerating("");
    }
  }

  const now = new Date();
  const filtered = items.filter((a) => {
    if (filter === "all") return true;
    const d = apptDate(a);
    if (filter === "completed") return a.completed;
    if (filter === "upcoming") return !a.completed && d && d >= now;
    if (filter === "past") return !a.completed && d && d < now;
    return true;
  });

  const counts = {
    all: items.length,
    upcoming: items.filter((a) => !a.completed && apptDate(a) && apptDate(a) >= now).length,
    past: items.filter((a) => !a.completed && apptDate(a) && apptDate(a) < now).length,
    completed: items.filter((a) => a.completed).length,
  };

  return (
    <div className="view-grid" data-testid="appointments-view">
      <div className="panel">
        <div className="panel-header">
          <Calendar size={18} weight="bold" />
          <span>APPOINTMENTS</span>
          <span className="header-count mono">{items.length}</span>
          <button
            className="btn btn--accent btn--sm ml-auto"
            onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="add-appt-btn"
          >
            <Plus size={12} weight="bold" /> NEW APPOINTMENT
          </button>
        </div>
        <div className="panel-body">
          <p className="panel-desc">Track every C&P exam, doctor visit, lab, hearing, and VSO meeting in one place. Hit <strong>PACKET</strong> on any appointment to print a single PDF you can take with you — covers your conditions, evidence highlights, active meds, and questions to ask.</p>
          <div className="rem-filter-row">
            {[
              ["all", "All"], ["upcoming", "Upcoming"], ["past", "Past due"], ["completed", "Completed"],
            ].map(([k, label]) => (
              <button
                key={k}
                className={`rem-filter-btn ${filter === k ? "rem-filter-btn--active" : ""}`}
                onClick={() => setFilter(k)}
                data-testid={`appt-filter-${k}`}
              >
                {label} <span className="mono">{counts[k]}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-body" style={{ padding: 0 }}>
          {loading && <div className="admin-empty">Loading...</div>}
          {!loading && filtered.length === 0 && (
            <div className="empty-state" data-testid="appt-empty">
              <Calendar size={28} weight="duotone" />
              <div className="empty-state-title">
                {filter === "all"
                  ? "No appointments yet"
                  : `No ${filter} appointments`}
              </div>
              <div className="empty-state-desc">
                Track every C&amp;P exam, doctor visit, lab, hearing, and VSO meeting in one place. Add one now and we'll auto-generate a day-of-exam packet plus a calendar reminder.
              </div>
              <button
                className="btn btn--accent"
                onClick={() => { setEditing(null); setShowForm(true); }}
                data-testid="appt-empty-cta"
              >
                <Plus size={12} weight="bold" /> ADD YOUR FIRST APPOINTMENT
              </button>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="table-scroll">
              <div className="table-scroll-hint">← scroll →</div>
            <table className="admin-table table--cards-on-mobile" data-testid="appt-table">
              <thead>
                <tr>
                  <th>WHEN</th>
                  <th>TYPE</th>
                  <th>TITLE</th>
                  <th>PROVIDER / LOCATION</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => {
                  const d = apptDate(a);
                  const meta = appointmentTypeMeta(a.type);
                  const status = apptStatus(a);
                  return (
                    <tr key={a.id} data-testid={`appt-row-${a.id}`}>
                      <td data-label="WHEN">
                        <div className="mono" style={{ fontWeight: 700 }}>{d ? d.toLocaleDateString() : "—"}</div>
                        <div className="small mono">{d ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</div>
                        <div className="small" style={{ marginTop: 2 }}>{d ? formatRelativeDay(d) : ""}</div>
                      </td>
                      <td data-label="TYPE">
                        <span className="appt-type-pill" style={{ background: meta.color }}>{meta.label}</span>
                      </td>
                      <td data-label="TITLE">
                        <div style={{ fontWeight: 700 }}>{a.title}</div>
                        {a.notes && <div className="small" style={{ marginTop: 4 }}>{a.notes}</div>}
                      </td>
                      <td data-label="PROVIDER">
                        {a.provider && <div className="small"><UserCircle size={11} weight="bold" /> {a.provider}</div>}
                        {a.location && <div className="small"><MapPin size={11} weight="bold" /> {a.location}</div>}
                      </td>
                      <td data-label="STATUS"><span className={STATUS_CLASS[status]}>{STATUS_LABEL[status]}</span></td>
                      <td>
                        <div className="row-actions">
                          <button
                            className="btn btn--accent btn--sm"
                            onClick={() => handleGeneratePacket(a)}
                            disabled={generating === a.id}
                            title="Generate Day-of-Exam Packet PDF"
                            data-testid={`appt-packet-${a.id}`}
                          >
                            <FilePdf size={11} weight="bold" /> {generating === a.id ? "..." : "PACKET"}
                          </button>
                          <button
                            className="btn btn--outline btn--sm"
                            onClick={() => handleGenerateWorksheets(a)}
                            disabled={generating === `ws-${a.id}`}
                            title="Generate printable patient/doctor worksheets for this appointment"
                            data-testid={`appt-worksheet-${a.id}`}
                          >
                            <Notepad size={11} weight="bold" /> {generating === `ws-${a.id}` ? "..." : "WORKSHEET"}
                          </button>
                          <button
                            className="btn btn--outline btn--sm"
                            onClick={() => downloadIcs(getAppointmentIcsUrl(a.id), `appointment-${(a.title || "appt").replace(/[^A-Za-z0-9_-]+/g, "-").slice(0, 30)}.ics`)}
                            title="Download .ics for calendar import"
                            data-testid={`appt-ics-${a.id}`}
                          >
                            <CalendarPlus size={11} weight="bold" />
                          </button>
                          <button className="btn btn--outline btn--sm" onClick={() => toggleCompleted(a)} title={a.completed ? "Mark as not done" : "Mark completed"} data-testid={`appt-complete-${a.id}`}>
                            {a.completed ? <Clock size={11} weight="bold" /> : <Check size={11} weight="bold" />}
                          </button>
                          <button className="btn btn--outline btn--sm" onClick={() => { setEditing(a); setShowForm(true); }} data-testid={`appt-edit-${a.id}`}>
                            <PencilSimple size={11} weight="bold" />
                          </button>
                          <button className="btn btn--danger btn--sm" onClick={() => handleDelete(a)} data-testid={`appt-delete-${a.id}`}>
                            <Trash size={11} weight="bold" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <AppointmentForm
          initial={editing ? { ...editing, datetime: editing.datetime?.slice(0, 16) } : null}
          issues={issues}
          defaultLinkedIssueId={issues[0]?.id || null}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSave={handleSave}
        />
      )}

      <button
        type="button"
        className="mobile-fab"
        onClick={() => { setEditing(null); setShowForm(true); }}
        aria-label="New appointment"
        data-testid="appt-mobile-fab"
      >
        <Plus size={20} weight="bold" />
      </button>
    </div>
  );
}
